import React, { useEffect, useState } from "react";
import { FaEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import Toast from '../../components/Toast/Toast';
import { addSocialMediaAPI, getSocialMediaAPI, deleteSocialMediaAPI, updateSocialMediaAPI } from '../../utils/api';
import Sidebar from "../Sidebar/Sidebar";
import AddSocialMediaModal from "./components/AddSocialMediaModal";
import { BASE_URL } from '../../utils/configConstants';
import "./SocialMedia.css";

const getMediaUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${BASE_URL}${path.replace(/^\//, '')}`;
};

const SocialMedia = () => {
    const navigate = useNavigate();
    const [socialMediaItems, setSocialMediaItems] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [toast, setToast] = useState({ show: false, message: "", type: "" });
    const [showModal, setShowModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [saveLoading, setSaveLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const PAGE_LIMIT = 10;

    const showToast = (message, type = "success") => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
    };

    const fetchSocialMedia = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getSocialMediaAPI({
                page,
                limit: PAGE_LIMIT,
                search: searchTerm.trim().toLowerCase() || "",
            });

            if (response?.status === 200) {
                setSocialMediaItems(response.data || []);
                setTotalPages(response?.pagination?.total_pages || 1);
            } else {
                setError(response?.message || 'Failed to fetch social media items');
            }
        } catch (err) {
            console.error('Fetch error:', err);
            setError('Failed to fetch social media items. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSocialMedia();
    }, [page, searchTerm]);

    const handleEdit = (item) => {
        setSelectedItem(item);
        setIsEditMode(true);
        setShowModal(true);
    };

    const handleAddNew = () => {
        setSelectedItem(null);
        setIsEditMode(false);
        setShowModal(true);
    };

    const handleSave = async (reqData) => {
        setSaveLoading(true);
        try {
            let response;
            if (isEditMode && selectedItem) {
                reqData.append('social_id', selectedItem.id);
                response = await updateSocialMediaAPI(reqData);
            } else {
                response = await addSocialMediaAPI(reqData);
            }

            if (response?.status === 200) {
                await fetchSocialMedia();
                setShowModal(false);
                showToast(`Social media ${isEditMode ? 'updated' : 'added'} successfully!`);
            } else {
                showToast(response?.message || 'Failed to save social media', "error");
            }
        } catch (err) {
            console.error('Save error:', err);
            showToast('An error occurred. Please try again.', "error");
        } finally {
            setSaveLoading(false);
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            const response = await deleteSocialMediaAPI({ social_id: itemToDelete.id });
            if (response?.status === 200) {
                setShowDeleteModal(false);
                showToast("Social media deleted successfully!");
                await fetchSocialMedia();
            } else {
                showToast(response?.message || 'Failed to delete social media', "error");
            }
        } catch (err) {
            console.error('Delete error:', err);
            showToast('An error occurred. Please try again.', "error");
        }
    };

    return (
        <div className="SocialMedia-layout">
            <Sidebar />
            <Toast toast={toast} />
            <div className="SocialMedia-main-content">
                <div className="SocialMedia-header-row">
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search by social type"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button className="insert-btn" onClick={handleAddNew}>
                        <span>Add Social Media</span>
                    </button>
                </div>

                {loading ? (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>Loading social media items...</p>
                    </div>
                ) : error ? (
                    <div className="loading-container">
                        <p style={{ color: '#dc2626' }}>{error}</p>
                    </div>
                ) : socialMediaItems.length === 0 ? (
                    <div className="empty-state">
                        <h3>No Social Media Found</h3>
                        <p>Click "Add Social Media" to create one.</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="SocialMedia-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Social Type</th>
                                    <th style={{ textAlign: "center" }}>URL</th>
                                    <th style={{ textAlign: "center" }}>Thumbnail</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {socialMediaItems.map((item, idx) => (
                                    <tr key={item.id || idx}>
                                        <td>{(page - 1) * PAGE_LIMIT + idx + 1}</td>
                                        <td style={{ textTransform: 'capitalize' }}>{item.social_type || item.type || item.name}</td>
                                        <td style={{ textAlign: "center" }}>
                                            <span style={{ display: 'block', wordBreak: 'break-all', color: '#475569' }}>
                                                {item.url}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="socialMedia-image-cell">
                                                {item.thumbnail ? (
                                                    <img 
                                                        src={getMediaUrl(item.thumbnail)} 
                                                        className="socialMedia-table-image"
                                                        alt="Thumbnail"
                                                    />
                                                ) : (
                                                    <div className="category-no-image">No Image</div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="socialMedia-action-btn-row">
                                                <button className="socialMedia-edit-btn" onClick={() => handleEdit(item)}>
                                                    <FaEdit />
                                                </button>
                                                <button className="socialMedia-delete-btn" onClick={() => { setItemToDelete(item); setShowDeleteModal(true); }}>
                                                    <MdDelete />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="table-footer">
                    <span>Showing {(page - 1) * PAGE_LIMIT + 1} to {Math.min(page * PAGE_LIMIT, socialMediaItems.length)} of {socialMediaItems.length} entries</span>
                    <div className="pagination">
                        <button disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
                        <button className="active">{page}</button>
                        <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
                    </div>
                </div>

                <AddSocialMediaModal
                    showModal={showModal}
                    isEditMode={isEditMode}
                    selectedItem={selectedItem}
                    saveLoading={saveLoading}
                    onSave={handleSave}
                    onClose={() => setShowModal(false)}
                />

                <ConfirmationModal
                    visible={showDeleteModal}
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => setShowDeleteModal(false)}
                    title="Confirm Deletion"
                    message="Are you sure you want to delete this social media entry?"
                    confirmText="Delete"
                />
            </div>
        </div>
    );
};

export default SocialMedia;
